/**
 * Outbound Webhooks Panel — Owner-only configuration UI on /tenants.
 *
 * Wires risk-rescore events (`risk_score_change`) to a customer's
 * Slack / PagerDuty / ITSM URL. Each row maps to one
 * `outbound_webhooks` document on the backend; we POST signed payloads
 * (HMAC SHA-256 via the optional shared secret) when a tenant's residual
 * score moves vs. yesterday by `min_delta` or more.
 *
 * Defensive moat: once on-call rotations consume our events, switching
 * costs go up materially.
 */
import { useEffect, useState } from "react";
import { Webhook, Plus, Trash2, PlayCircle, Power, CheckCircle2, XCircle } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const DEFAULT_TENANT = "tnt_default";

function StatRow({ label, value, mono = false }) {
  return (
    <div className="flex items-center justify-between gap-3 py-0.5 text-[11px]">
      <span className="text-ek-ink/55">{label}</span>
      <span className={`text-ek-ink ${mono ? "font-mono" : "font-semibold"}`}>{value ?? "—"}</span>
    </div>
  );
}

export function OutboundWebhooksPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [draftOpen, setDraftOpen] = useState(false);
  const [draft, setDraft] = useState({
    url: "", secret: "", tenant_id: DEFAULT_TENANT,
    min_delta: 1.0, active: true,
  });

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const r = await api.get("/owner/webhooks");
      setItems(r.data.rows || []);
    } catch (e) {
      setErr(formatApiErrorDetail(e));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!draft.url) { toast.error("Webhook URL is required"); return; }
    try {
      await api.post("/owner/webhooks", {
        url: draft.url, secret: draft.secret || null,
        tenant_id: draft.tenant_id || DEFAULT_TENANT,
        min_delta: Number(draft.min_delta) || 1.0,
        active: !!draft.active, events: ["risk_score_change"],
      });
      toast.success("Webhook created");
      setDraftOpen(false);
      setDraft({ url: "", secret: "", tenant_id: DEFAULT_TENANT, min_delta: 1.0, active: true });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e) || "Create failed"); }
  };

  const toggle = async (wh) => {
    try {
      await api.patch(`/owner/webhooks/${wh.webhook_id}`, { active: !wh.active });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
  };

  const fireTest = async (wh) => {
    try {
      const r = await api.post(`/owner/webhooks/${wh.webhook_id}/test`);
      toast.success(`Synthetic event dispatched (sent=${r.data.dispatched}, failed=${r.data.failed})`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e) || "Test failed"); }
  };

  const remove = async (wh) => {
    if (!window.confirm(`Delete webhook for ${wh.url}?`)) return;
    try {
      await api.delete(`/owner/webhooks/${wh.webhook_id}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
  };

  return (
    <section
      className="rounded-2xl border border-black/5 bg-white p-5 shadow-sm"
      data-testid="outbound-webhooks-panel"
    >
      <div className="flex items-start gap-3">
        <div className="rounded-lg bg-ek-purple/10 p-2 text-ek-purple">
          <Webhook size={18} />
        </div>
        <div className="flex-1">
          <h2 className="font-display text-lg font-semibold text-ek-ink">
            Outbound risk-rescore webhooks
          </h2>
          <p className="mt-0.5 text-sm text-ek-ink/65">
            POST signed <code className="font-mono text-[11px]">risk_score_change</code> events to
            a customer&apos;s Slack / PagerDuty / ITSM URL when a tenant&apos;s residual score moves
            ≥ <code className="font-mono text-[11px]">min_delta</code>. Optional shared
            secret produces a <code className="font-mono text-[11px]">X-ERIO-Signature: sha256=…</code> header
            so the consumer can verify authenticity.
          </p>
        </div>
        <button
          onClick={() => setDraftOpen((v) => !v)}
          className="inline-flex items-center gap-1.5 rounded-md bg-ek-purple px-3 py-1.5 text-xs font-semibold text-white hover:bg-ek-purple/90"
          data-testid="webhook-add-toggle"
        >
          <Plus size={14} /> Add webhook
        </button>
      </div>

      {draftOpen && (
        <div className="mt-4 grid gap-2 rounded-xl border border-black/10 bg-slate-50/60 p-3 text-xs sm:grid-cols-2" data-testid="webhook-create-form">
          <label className="space-y-1 sm:col-span-2">
            <span className="text-ek-ink/65">Webhook URL (https://…)</span>
            <input
              value={draft.url}
              onChange={(e) => setDraft({ ...draft, url: e.target.value })}
              className="w-full rounded-md border border-black/10 px-2 py-1.5 font-mono text-xs focus:border-ek-purple focus:outline-none"
              placeholder="https://hooks.slack.com/services/T0/B0/XXX"
              data-testid="webhook-url-input"
            />
          </label>
          <label className="space-y-1">
            <span className="text-ek-ink/65">Shared secret (optional, signs payload)</span>
            <input
              value={draft.secret}
              onChange={(e) => setDraft({ ...draft, secret: e.target.value })}
              className="w-full rounded-md border border-black/10 px-2 py-1.5 font-mono text-xs focus:border-ek-purple focus:outline-none"
              placeholder="(blank = no signature)"
              data-testid="webhook-secret-input"
            />
          </label>
          <label className="space-y-1">
            <span className="text-ek-ink/65">Tenant</span>
            <input
              value={draft.tenant_id}
              onChange={(e) => setDraft({ ...draft, tenant_id: e.target.value })}
              className="w-full rounded-md border border-black/10 px-2 py-1.5 font-mono text-xs focus:border-ek-purple focus:outline-none"
              data-testid="webhook-tenant-input"
            />
          </label>
          <label className="space-y-1">
            <span className="text-ek-ink/65">Min delta (residual points)</span>
            <input
              type="number" step="0.1" min="0.1"
              value={draft.min_delta}
              onChange={(e) => setDraft({ ...draft, min_delta: e.target.value })}
              className="w-full rounded-md border border-black/10 px-2 py-1.5 text-xs focus:border-ek-purple focus:outline-none"
              data-testid="webhook-delta-input"
            />
          </label>
          <div className="flex items-center justify-end gap-2 sm:col-span-2">
            <button
              onClick={() => setDraftOpen(false)}
              className="rounded-md border border-black/10 px-2.5 py-1 text-xs font-semibold text-ek-ink/70 hover:text-ek-ink"
            >Cancel</button>
            <button
              onClick={create}
              className="rounded-md bg-ek-ink px-2.5 py-1 text-xs font-semibold text-white hover:bg-black"
              data-testid="webhook-create-submit"
            >Create</button>
          </div>
        </div>
      )}

      {err && <p className="mt-3 text-xs text-rose-600" data-testid="webhook-error">{err}</p>}

      <div className="mt-4 space-y-2">
        {loading && <p className="text-xs text-ek-ink/50">Loading…</p>}
        {!loading && items.length === 0 && (
          <p className="text-xs text-ek-ink/50" data-testid="webhook-empty">
            No webhooks configured yet. Add one above to wire risk events into Slack, PagerDuty or ITSM.
          </p>
        )}
        {items.map((wh) => (
          <div
            key={wh.webhook_id}
            className="rounded-xl border border-black/5 bg-slate-50/40 p-3"
            data-testid={`webhook-row-${wh.webhook_id}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="truncate font-mono text-xs text-ek-ink" title={wh.url}>{wh.url}</p>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px]">
                  <span className="font-mono text-ek-ink/55">{wh.tenant_id}</span>
                  <span className="text-ek-ink/40">·</span>
                  <span className="text-ek-ink/55">min delta {wh.min_delta}</span>
                  <span className="text-ek-ink/40">·</span>
                  <span className={`rounded px-1.5 py-0.5 font-semibold ${wh.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>
                    {wh.active ? "active" : "paused"}
                  </span>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <button
                  onClick={() => fireTest(wh)}
                  title="Send synthetic test event"
                  className="rounded-md border border-black/10 p-1.5 text-ek-ink/70 hover:border-ek-purple/40 hover:text-ek-purple"
                  data-testid={`webhook-test-${wh.webhook_id}`}
                ><PlayCircle size={14} /></button>
                <button
                  onClick={() => toggle(wh)}
                  title={wh.active ? "Pause" : "Resume"}
                  className="rounded-md border border-black/10 p-1.5 text-ek-ink/70 hover:border-ek-purple/40 hover:text-ek-purple"
                  data-testid={`webhook-toggle-${wh.webhook_id}`}
                ><Power size={14} /></button>
                <button
                  onClick={() => remove(wh)}
                  title="Delete"
                  className="rounded-md border border-black/10 p-1.5 text-rose-600/80 hover:border-rose-300 hover:text-rose-600"
                  data-testid={`webhook-delete-${wh.webhook_id}`}
                ><Trash2 size={14} /></button>
              </div>
            </div>
            <div className="mt-2 grid gap-x-4 sm:grid-cols-3">
              <StatRow label="Delivered" value={wh.delivery_stats?.sent ?? 0} />
              <StatRow label="Failed" value={wh.delivery_stats?.failed ?? 0} />
              <StatRow label="Last status"
                value={
                  wh.delivery_stats?.last_status
                    ? (
                      <span className={`inline-flex items-center gap-1 ${wh.delivery_stats.last_status < 300 ? "text-emerald-700" : "text-rose-600"}`}>
                        {wh.delivery_stats.last_status < 300 ? <CheckCircle2 size={11} /> : <XCircle size={11} />}
                        {wh.delivery_stats.last_status}
                      </span>
                    )
                    : "—"
                }
              />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
